class SecretApp {

    /** @type {Object} window.i18n injected by the PHP layout. */
    #i18n;



    /**
     * Boot the app: load translations and wire up all page features.
     */
    constructor() {
        
        // Load translations
        const i18nMeta = document.querySelector('meta[name="i18n"]');
        this.#i18n = i18nMeta ? JSON.parse(i18nMeta.content) : {};
        
        // Initialize features
        this.#initEyeToggle();
        this.#initCharCounter();
        this.#initSecretInputAutofocus();
        this.#initCopyLink();
        this.#initCopyContent();
        this.#initDeleteConfirm();
        this.#initShareLink();
        this.#initKeyPreserve();
        this.#initDecrypt();
        this.#initEncrypt();

    }



    /**
     * Toggle password visibility on all .input_password_wrapper buttons.
     *
     * @return {void}
     */
    #initEyeToggle() {

        document.querySelectorAll('.input_password_wrapper button').forEach(button => {
            button.addEventListener('click', () => {
                const input = button.previousElementSibling;
                const icon = button.querySelector('i');

                if (input.type === 'password') {
                    input.type = 'text';
                    icon.classList.replace('fa-eye-slash', 'fa-eye');
                } else {
                    input.type = 'password';
                    icon.classList.replace('fa-eye', 'fa-eye-slash');
                }
            });
        });
    }



    /**
     * Count Unicode code points (not UTF-16 code units) so it matches
     * the server-side mb_strlen() validation.
     *
     * @param {string} str
     * @return {number}
     */
    #countChars(str) {
        return [...str].length;
    }



    /**
     * Focus #secretInput on load for pointer devices only (skip touch-primary screens).
     *
     * @return {void}
     */
    #initSecretInputAutofocus() {

        const textarea = document.getElementById('secretInput');
        const isMobileDevice = window.matchMedia('(hover: none) and (pointer: coarse)').matches;

        if (! textarea || isMobileDevice) {
            return;
        }

        textarea.focus();
    }



    /**
     * Wire up the character counter and hard limit for #secretInput.
     *
     * @return {void}
     */
    #initCharCounter() {

        const textarea = document.getElementById('secretInput');
        const charCount = document.getElementById('charCount');

        if (! textarea || ! charCount) return;

        const update = () => {
            const chars = [...textarea.value];

            if (chars.length > this.#i18n.maxChars) {
                textarea.value = chars.slice(0, this.#i18n.maxChars).join('');
            }

            const current = this.#countChars(textarea.value);
            charCount.textContent = current + ' / ' + this.#i18n.maxChars + ' ' + this.#i18n.charCountUnit;
            charCount.classList.toggle('is-limit', current >= this.#i18n.maxChars);
        };

        update();
        this.#toggleSubmitButton();
        textarea.addEventListener('input', () => {
            update();
            this.#toggleSubmitButton();
        });
    }



    /**
     * Enable or disable the create-form submit button based on form state.
     *
     * Disabled when the textarea is empty or the password length is out of range.
     *
     * @return {void}
     */
    #toggleSubmitButton() {

        const textarea = document.getElementById('secretInput');
        const passwordInput = document.getElementById('passwordInput');
        const form = document.querySelector('form[action*="secret/create"]');
        const btn = form?.querySelector('[type="submit"]');

        if (! btn || ! textarea) {
            return;
        }

        const passwordLen = passwordInput ? [...passwordInput.value].length : 0;
        const passwordInvalid = passwordLen > 0 && (
            passwordLen < this.#i18n.passwordMinLength
            ||
            passwordLen > this.#i18n.passwordMaxLength
        );

        btn.disabled = ! textarea.value.trim() || passwordInvalid;
    }



    /**
     * Swap button label to a success state, then restore it after 2 s.
     *
     * @param {HTMLElement} btn
     * @param {string} successHtml
     * @param {string} resetHtml
     * @return {void}
     */
    #setCopyFeedback(btn, successHtml, resetHtml) {

        btn.innerHTML = successHtml;
        setTimeout(() => { btn.innerHTML = resetHtml; }, 2000);
    }



    /**
     * Copy the share link to the clipboard.
     *
     * @return {void}
     */
    #initCopyLink() {

        const copyBtn = document.querySelector('.link_share_box .button');
        const shareLink = document.getElementById('share_link');

        if (! copyBtn || ! shareLink) {
            return;
        }

        copyBtn.addEventListener('click', async (e) => {
            e.preventDefault();

            await navigator.clipboard.writeText(shareLink.value);

            this.#setCopyFeedback(
                copyBtn,
                '<i class="fa-regular fa-check"></i> ' + this.#i18n.copied,
                '<i class="fa-regular fa-copy"></i> ' + this.#i18n.copyLink
            );
        });
    }



    /**
     * Copy the decrypted secret text to the clipboard.
     *
     * @return {void}
     */
    #initCopyContent() {

        const btn = document.querySelector('.copy_content_btn');

        if (! btn) {
            return;
        }

        btn.addEventListener('click', async (e) => {
            e.preventDefault();

            const text = document.getElementById('secretContent').innerText;

            await navigator.clipboard.writeText(text);

            this.#setCopyFeedback(
                btn,
                '<i class="fa-regular fa-check"></i><span>' + this.#i18n.copied + '</span>',
                '<i class="fa-regular fa-copy"></i><span>' + this.#i18n.copyContent + '</span>'
            );
        });
    }



    /**
     * Ask for confirmation before submitting any destroy form.
     *
     * @return {void}
     */
    #initDeleteConfirm() {

        document.querySelectorAll('form[action*="destroy"]').forEach(form => {
            form.addEventListener('submit', (e) => {

                if (! confirm(this.#i18n.deleteConfirm)) {
                    e.preventDefault();
                }

            });
        });
    }



    /**
     * Return a create-form validation error or null when valid.
     *
     * @return {{ message: string, field: 'secret'|'password' }|null}
     */
    #getCreateFormError() {

        const textarea = document.getElementById('secretInput');
        const passwordInput = document.getElementById('passwordInput');

        if (textarea) {
            const secret = textarea.value;

            if (! secret.trim()) {
                return { message: this.#i18n.secretEmpty, field: 'secret' };
            }

            if (this.#countChars(secret) > this.#i18n.maxChars) {
                return { message: this.#i18n.secretTooLong, field: 'secret' };
            }
        }

        if (passwordInput) {
            const length = [...passwordInput.value].length;

            if (length > 0 && length < this.#i18n.passwordMinLength) {
                return { message: this.#i18n.passwordTooShort, field: 'password' };
            }

            if (length > this.#i18n.passwordMaxLength) {
                return { message: this.#i18n.passwordTooLong, field: 'password' };
            }
        }

        return null;
    }



    /**
     * Show or hide the inline create-form validation message.
     *
     * @param {{ message: string, field: 'secret'|'password' }|null} error
     * @param {boolean} focusField Move focus to the affected field
     * @return {void}
     */
    #setFormValidation(error, focusField = false) {

        const box = document.getElementById('formValidationError');
        const textarea = document.getElementById('secretInput');
        const passwordInput = document.getElementById('passwordInput');
        const messageEl = box?.querySelector('span');

        if (! box || ! messageEl) {
            return;
        }

        textarea?.closest('.textarea_box')?.classList.remove('has-error');
        passwordInput?.closest('.input_password_wrapper')?.classList.remove('has-error');

        if (! error) {
            box.hidden = true;
            return;
        }

        const fieldEl = error.field === 'secret' ? textarea : passwordInput;
        const wrapperSelector = error.field === 'secret'
            ? '.textarea_box'
            : '.input_password_wrapper';

        messageEl.textContent = error.message;
        box.hidden = false;
        fieldEl?.closest(wrapperSelector)?.classList.add('has-error');

        if (focusField) {
            fieldEl?.focus();
            box.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }



    /**
     * Calculate password strength on a 0–4 scale.
     *
     * Scoring: +1 per criterion (length ≥ 8, length ≥ 12, uppercase, digit, special char).
     * Score 0 = empty, 1 = weak, 2 = fair, 3 = good, 4 = strong.
     *
     * @param {string} password
     * @return {number}
     */
    #getPasswordStrength(password) {

        if (! password) {
            return 0;
        }

        let score = 0;

        if (password.length >= 8) score++;
        if (password.length >= 12) score++;
        if (/[A-Z]/.test(password)) score++;
        if (/[0-9]/.test(password)) score++;
        if (/[^A-Za-z0-9]/.test(password)) score++;

        if (score <= 1) return 1;
        if (score <= 2) return 2;
        if (score <= 3) return 3;

        return 4;
    }



    /**
     * Render the password strength bar and label for the given password.
     *
     * @param {string} password
     * @return {void}
     */
    #updatePasswordStrength(password) {

        const el = document.getElementById('passwordStrength');

        if (! el) {
            return;
        }

        const fill = el.querySelector('.password_strength_fill');
        const label = el.querySelector('.password_strength_label');
        const len = [...password].length;

        const render = (width, color, text) => {
            fill.style.width = width;
            fill.style.backgroundColor = color;
            label.textContent = text;
        };

        if (! password) {
            render('0', '', '\u00a0');
            return;
        }

        if (len < this.#i18n.passwordMinLength) {
            render('15%', 'var(--state-red)', this.#i18n.passwordStrengthTooShort);
            return;
        }

        if (len > this.#i18n.passwordMaxLength) {
            render('15%', 'var(--state-red)', this.#i18n.passwordStrengthTooLong);
            return;
        }

        const levels = [
            null,
            { width: '25%', color: 'var(--state-red)',    text: this.#i18n.passwordStrengthWeak },
            { width: '50%', color: 'var(--state-yellow)', text: this.#i18n.passwordStrengthFair },
            { width: '75%', color: '#7EC636',             text: this.#i18n.passwordStrengthGood },
            { width: '100%', color: 'var(--state-green)', text: this.#i18n.passwordStrengthStrong },
        ];

        const config = levels[this.#getPasswordStrength(password)];
        render(config.width, config.color, config.text);
    }



    /**
     * Encrypt the plaintext with AES-GCM in the browser before form submission
     * and store the Base64url key in sessionStorage for the success page.
     *
     * @return {void}
     */
    #initEncrypt() {

        const form = document.querySelector('form[action*="secret/create"]');
        const cipherInput = document.getElementById('secretCipher');
        const textarea = document.getElementById('secretInput');
        const passwordInput = document.getElementById('passwordInput');

        if (! form || ! cipherInput || ! textarea) return;

        passwordInput?.addEventListener('input', () => {
            this.#updatePasswordStrength(passwordInput.value);
            this.#toggleSubmitButton();
        });

        form.addEventListener('submit', async (e) => {
            e.preventDefault();

            const error = this.#getCreateFormError();

            if (error) {
                this.#setFormValidation(error, true);
                return;
            }

            this.#setFormValidation(null);

            if (! window.crypto?.subtle) {
                alert(this.#i18n.httpsRequired);
                return;
            }

            const plaintext = textarea.value;

            try {
                const key = await crypto.subtle.generateKey(
                    { name: 'AES-GCM', length: 256 },
                    true,
                    ['encrypt', 'decrypt']
                );

                const iv = crypto.getRandomValues(new Uint8Array(12));
                const encoded = new TextEncoder().encode(plaintext);
                const encrypted = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, encoded);
                const rawKey = await crypto.subtle.exportKey('raw', key);
                const toB64 = (buf) => btoa(String.fromCharCode(...new Uint8Array(buf)));

                // Base64url encode key (URL-safe, no padding) for use in URL fragment
                const keyB64url = toB64(rawKey)
                    .replace(/\+/g, '-')
                    .replace(/\//g, '_')
                    .replace(/=/g, '');

                cipherInput.value = JSON.stringify({
                    iv: toB64(iv.buffer),
                    data: toB64(encrypted)
                });

                sessionStorage.setItem('pending_key', keyB64url);

                form.submit();

            } catch {
                // Crypto API unavailable – submit with empty ciphertext so server returns validation error
                form.submit();
            }
        });
    }



    /**
     * Inject the ZK key from sessionStorage into the share link input.
     *
     * @return {void}
     */
    #initShareLink() {

        const input = document.getElementById('share_link');

        if (! input) {
            return;
        }

        const pendingKey = sessionStorage.getItem('pending_key');

        if (pendingKey) {
            input.value = input.value + '#key=' + pendingKey;
        } else {
            input.value = '';
            input.placeholder = this.#i18n.keyMissing;
        }
    }



    /**
     * Resolve the decryption key from the URL fragment.
     *
     * @return {string|null}
     */
    #resolveKey() {

        const match = window.location.hash.match(/[#&]key=([^&]+)/);
        return match ? match[1] : null;
    }



    /**
     * Append the URL fragment to reveal/verify forms on submit so the key
     * survives POST → redirect → GET. Read at submit time so a hash added
     * after page load is still included.
     *
     * @return {void}
     */
    #initKeyPreserve() {

        document.querySelectorAll(
            'form[action*="secret/reveal"], form[action*="secret/verify"]'
        ).forEach(form => {
            form.addEventListener('submit', () => {
                const hash = window.location.hash;

                if (! hash) {
                    return;
                }

                const baseAction = form.getAttribute('action').split('#')[0];
                form.setAttribute('action', baseAction + hash);
            });
        });
    }



    /**
     * Redirect to the failed page when the ZK key is missing or decryption fails
     *
     * @return {void}
     */
    #redirectToFailed() {

        const secretEl = document.getElementById('secretContent');
        const failedUrl = secretEl?.dataset.failedUrl;

        if (failedUrl) {
            window.location.replace(failedUrl);
        }
    }



    /**
     * Decrypt the AES-GCM ciphertext stored in data-ciphertext using
     * the Base64url key from the URL fragment.
     *
     * @return {Promise<void>}
     */
    async #initDecrypt() {

        const secretEl = document.getElementById('secretContent');

        if (! secretEl || ! secretEl.dataset.ciphertext) {
            return;
        }

        const secretKey = this.#resolveKey();

        if (! secretKey) {
            this.#redirectToFailed();
            return;
        }

        try {

            const parsed = JSON.parse(secretEl.dataset.ciphertext);
            const fromB64 = (b64) => Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
            const iv = fromB64(parsed.iv);
            const data = fromB64(parsed.data);
            const rawKey = fromB64(secretKey.replace(/-/g, '+').replace(/_/g, '/'));

            const key = await crypto.subtle.importKey(
                'raw',
                rawKey,
                { name: 'AES-GCM' },
                false,
                ['decrypt']
            );

            const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, data);
            const text = new TextDecoder().decode(decrypted);

            // Safe XSS-free insertion preserving newlines
            secretEl.textContent = '';
            text.split('\n').forEach((line, i) => {
                if (i > 0) secretEl.appendChild(document.createElement('br'));
                secretEl.appendChild(document.createTextNode(line));
            });

            if (secretEl.dataset.pendingDestroy === '1') {
                await this.#acknowledgeView(secretEl);
            }

        } catch {
            this.#redirectToFailed();
        }
    }



    /**
     * Tell the server to delete a self-destruct secret after successful decryption
     *
     * @param {HTMLElement} secretEl
     * @return {Promise<void>}
     */
    async #acknowledgeView(secretEl) {

        const ackUrl = secretEl.dataset.ackUrl;
        const csrfToken = secretEl.dataset.csrfToken;
        const csrfHeader = secretEl.dataset.csrfHeader;

        if (! ackUrl || ! csrfToken || ! csrfHeader) {
            return;
        }

        try {
            await fetch(ackUrl, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    [csrfHeader]: csrfToken,
                    'X-Requested-With': 'XMLHttpRequest',
                },
            });
        } catch {
            // Non-blocking: user already sees the plaintext; cleanup can retry on reload
        }
    }


}


// Bootstrap the app
document.addEventListener('DOMContentLoaded', () => new SecretApp());